

/** Endpoint can be a constant string or a late-bound function (e.g. config-driven). */
export type GqlEndpoint = string | (() => string);

export interface GqlRequest {
	query: string;
	variables?: Record<string, unknown>;
	operationName?: string;
	signal?: AbortSignal;
}

export interface GqlError {
	message: string;
	locations?: Array<{ line: number; column: number }>;
	path?: Array<string | number>;
	extensions?: Record<string, unknown>;
}

export interface GqlResponse<T = unknown> {
	data?: T;
	errors?: GqlError[];
}

/** Result of {@link GqlClient.checkStatus}. */
export interface Status {
	/** True iff the endpoint accepted a trivial query and returned no GraphQL errors. */
	ok: boolean;
	httpStatus?: number;
	errors?: GqlError[];
	/** Set when a transport-level failure prevented a GraphQL round-trip. */
	transportError?: string;
}
/**
 * Neutral GraphQL client.
 *
 * Makes no choices about how to unwrap data or treat GraphQL-level `errors`.
 * Transport-level failures (non-2xx HTTP) throw; everything else is handed
 * back to the caller verbatim so each feature can pick its own policy
 * (throw on errors, surface as warnings, return envelope, etc.).
 * 
 * NOTE: this client specifically does not manage caching of requests or responses; that implementation
 * is left to the caller. 
 */
export class GqlClient {
	constructor(
		// Only the wrapped `fetch` is needed here; the global `csrf` (CsrfFetch) satisfies it.
		private readonly csrf: Pick<CsrfFetch, 'fetch'>,
		private readonly gqlEndpoint: GqlEndpoint,
	) {}

	/**
	 * Execute a GraphQL request. Throws on HTTP failure, otherwise returns the
	 * raw response body as `T`. Callers typically pass `GqlResponse<TData>`.
	 */
	async execute<T = GqlResponse<unknown>>(request: GqlRequest): Promise<T> {
		const response = await this.csrf.fetch(this.resolveEndpoint(), {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				query: request.query,
				variables: request.variables,
				operationName: request.operationName,
			}),
			signal: request.signal,
		});
		if (!response.ok) {
			throw new Error(`GraphQL HTTP ${response.status} ${response.statusText}`);
		}
		return response.json() as Promise<T>;
	}

	/**
	 * Probe the endpoint with `{ __typename }`. Never throws — transport errors
	 * and GraphQL errors are both surfaced on the returned {@link Status}.
	 */
	async checkStatus(signal?: AbortSignal): Promise<Status> {
		try {
			const response = await this.csrf.fetch(this.resolveEndpoint(), {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ query: '{ __typename }' }),
				signal,
			});
			if (!response.ok) {
				return {
					ok: false,
					httpStatus: response.status,
					transportError: `HTTP ${response.status} ${response.statusText}`,
				};
			}
			const body = await response.json() as GqlResponse<{ __typename: string }>;
			if (body.errors && body.errors.length) {
				return { ok: false, httpStatus: response.status, errors: body.errors };
			}
			return { ok: true, httpStatus: response.status };
		} catch (e) {
			return { ok: false, transportError: e instanceof Error ? e.message : String(e) };
		}
	}

	private resolveEndpoint(): string {
		return typeof this.gqlEndpoint === 'function' ? this.gqlEndpoint() : this.gqlEndpoint;
	}
}
